// src/views/contasReceber/ContasReceberList.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import contasReceberService from "../../services/contasReceberService";
import { useAuth } from "../../contexts/AuthContext";
import ContaReceberModal from "./ContaReceberModal";
import ReceberParcelaModal from "./ReceberParcelaModal";
import DeleteModal from "../../components/ui/DeleteModal";
import Banner from "../../components/ui/Banner";

const fmtMoeda = v => v != null
  ? Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
  : "—";
const fmtDt = dt => {
  if (!dt) return "—";
  try { return new Date(String(dt).split("T")[0] + "T12:00:00").toLocaleDateString("pt-BR"); }
  catch { return dt; }
};

const STATUS_PAR = {
  Aberto:    { background:"var(--bg-warning-soft)", color: "#854D0E", label: "Em Aberto" },
  Recebido:  { background:"var(--bg-success-soft)", color: "#166534", label: "Recebido" },
  Vencido:   { background:"var(--bg-danger-soft)", color: "#991B1B", label: "Vencido" },
  Cancelado: { background:"var(--bg-page)", color:"var(--gray-500)", label: "Cancelado" },
};

const TP_RECEITA_LABELS = {
  CotaCondominial: "Cota Condominial",
  Multa: "Multa",
  Acordo: "Acordo",
  Locacao: "Locação",
  Caucao: "Caução",
  ReceitaFinanceira: "Rec. Financeira",
  Ressarcimento: "Ressarcimento",
  Outro: "Outro",
};

const ORIGEM_META = {
  Cota:       { icon: "bi-receipt",         color: "#1E40AF", bg: "#DBEAFE", label: "Cota" },
  Multa:      { icon: "bi-exclamation-triangle", color: "#9A3412", bg: "#FED7AA", label: "Multa" },
  Reserva:    { icon: "bi-calendar-check",  color: "#6B21A8", bg: "#F3E8FF", label: "Reserva" },
  AguaGas:    { icon: "bi-droplet-half",    color: "#0E7490", bg: "#CFFAFE", label: "Água/Gás" },
  Recorrente: { icon: "bi-arrow-repeat",    color: "#365314", bg: "#ECFCCB", label: "Recorrente" },
  Acordo:     { icon: "bi-handshake",       color: "#7C2D12", bg: "#FDE68A", label: "Acordo" },
  Avulso:     { icon: "bi-pencil-square",   color:"var(--gray-600)", bg: "#F1F5F9", label: "Avulso" },
};

const PER_PAGE = 15;

// Helper — dias entre uma data e hoje (negativo = atrasado)
function diasParaHoje(dt) {
  if (!dt) return null;
  const a = new Date(String(dt).split("T")[0] + "T00:00:00");
  const b = new Date(); b.setHours(0, 0, 0, 0);
  return Math.floor((a - b) / 86400000);
}

function badgeDias(parcela) {
  if (!parcela || parcela.status === "Recebido" || parcela.status === "Cancelado") return null;
  const d = diasParaHoje(parcela.dtVencimento);
  if (d === null) return null;
  if (d < 0) return { txt: `Atrasado ${Math.abs(d)}d`, color: "#991B1B", background:"var(--bg-danger-soft)" };
  if (d === 0) return { txt: "Vence hoje", color: "#9A3412", background: "#FED7AA" };
  if (d <= 7)  return { txt: `Em ${d}d`, color: "#92400E", background: "#FEF3C7" };
  return null;
}

export default function ContasReceberList() {
  const { condominioAtivo, podeEditar } = useAuth();
  const canEdit = podeEditar("contasReceber");

  const [aba, setAba] = useState("lancamentos");

  const [idCondo, setIdCondo] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [totais, setTotais] = useState({});
  const [filtros, setFiltros] = useState({
    dtVencIni: "", dtVencFim: "",
    status: "", tpReceita: "", tpOrigem: "",
    idCondomino: "", idUnidade: "", search: "",
    page: 1,
  });
  const [expandidos, setExpandidos] = useState({});

  // Relatório
  const [relFiltros, setRelFiltros] = useState({
    dtVencIni: "", dtVencFim: "", status: "", tpReceita: "", agrupar: "tpReceita",
  });
  const [relData, setRelData] = useState(null);
  const [relLoading, setRelLoading] = useState(false);

  // Dashboard
  const [dashData, setDashData] = useState(null);
  const [dashLoading, setDashLoading] = useState(false);

  // Modais
  const [showModal, setShowModal]       = useState(false);
  const [editData, setEditData]         = useState(null);
  const [showReceber, setShowReceber]   = useState(false);
  const [parcelaReceber, setParcelaReceber] = useState(null);
  const [parcelaDescricao, setParcelaDescricao] = useState("");
  const [parcelaCobradoBoleto, setParcelaCobradoBoleto] = useState(false);
  const [showCancel, setShowCancel]     = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [saving, setSaving]             = useState(false);
  const [cancelling, setCancelling]     = useState(false);

  // Banner global de feedback (sucesso/erro/info)
  const [banner, setBanner] = useState(null);
  const showBanner = (tipo, msg) => { setBanner({ tipo, msg }); setTimeout(()=>setBanner(null), 6000); };

  // Estorno com motivo (substitui window.prompt)
  const [showEstornar, setShowEstornar] = useState(false);
  const [estornarTarget, setEstornarTarget] = useState(null);
  const [estornarMotivo, setEstornarMotivo] = useState("");
  const [estornarSaving, setEstornarSaving] = useState(false);

  // Sincroniza com o condomínio ativo no navbar
  useEffect(() => {
    if (condominioAtivo?.id) setIdCondo(String(condominioAtivo.id));
    else setIdCondo("");
  }, [condominioAtivo]);

  const carregar = useCallback(async () => {
    if (!idCondo) return;
    setLoading(true);
    try {
      const r = await contasReceberService.listar({
        idCondominio: idCondo,
        ...filtros,
        limit: PER_PAGE,
      });
      setData(r.data || []);
      setPagination(r.pagination || { total: 0, totalPages: 1 });
      setTotais(r.totais || {});
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [idCondo, filtros]);

  useEffect(() => { if (aba === "lancamentos" && idCondo) carregar(); }, [carregar, aba, idCondo]);

  const setF = (f, v) => setFiltros(p => ({ ...p, [f]: v, page: 1 }));

  const condoNome = condominioAtivo?.nmFantasia || condominioAtivo?.nmCondominio || "";

  const handleSave = async (form) => {
    setSaving(true);
    try {
      const payload = { ...form, idCondominio: idCondo };
      if (form.id) await contasReceberService.atualizar(form.id, payload);
      else         await contasReceberService.criar(payload);
      setShowModal(false);
      showBanner("success", form.id ? "Lançamento atualizado." : "Lançamento criado com sucesso.");
      await carregar();
    } catch (e) {
      showBanner("error", e.message || "Erro ao salvar.");
    } finally { setSaving(false); }
  };

  const handleCancelar = async () => {
    setCancelling(true);
    try {
      await contasReceberService.cancelar(cancelTarget.id);
      setShowCancel(false);
      setCancelTarget(null);
      showBanner("success", "Lançamento cancelado.");
      await carregar();
    } catch (e) { showBanner("error", e.message || "Erro ao cancelar."); }
    finally { setCancelling(false); }
  };

  const handleRecebido = async (formRec) => {
    try {
      await contasReceberService.receberParcela(parcelaReceber.id, formRec);
      setShowReceber(false);
      setParcelaReceber(null);
      showBanner("success", "Recebimento registrado com sucesso.");
      await carregar();
    } catch (e) {
      showBanner("error", e.message || "Não foi possível registrar o recebimento.");
    }
  };

  const abrirEstornar = (parcela) => {
    setEstornarTarget(parcela);
    setEstornarMotivo("");
    setShowEstornar(true);
  };

  const confirmarEstorno = async () => {
    if (!estornarMotivo.trim()) {
      showBanner("warning", "Informe um motivo para o estorno.");
      return;
    }
    setEstornarSaving(true);
    try {
      await contasReceberService.estornarRecebimento(estornarTarget.id, { motivo: estornarMotivo });
      setShowEstornar(false);
      setEstornarTarget(null);
      showBanner("success", "Recebimento estornado com sucesso.");
      await carregar();
    } catch (e) { showBanner("error", e.message || "Erro ao estornar."); }
    finally { setEstornarSaving(false); }
  };

  const handleDuplicar = (cr) => {
    if (cr.tpOrigem && cr.tpOrigem !== "Avulso") {
      showBanner("warning", "Apenas lançamentos avulsos podem ser duplicados.");
      return;
    }
    setEditData({
      ...cr, id: undefined, status: undefined,
      vlrRecebido: 0, dtEmissao: "", dtCompetencia: "",
      parcelas: undefined, _duplicar: true,
    });
    setShowModal(true);
  };

  const gerarRelatorio = async () => {
    if (!idCondo) return;
    setRelLoading(true);
    try {
      const r = await contasReceberService.relatorio({ idCondominio: idCondo, ...relFiltros });
      setRelData(r);
    } catch (e) { showBanner("error", e.message || "Erro ao gerar relatório."); }
    finally { setRelLoading(false); }
  };

  const carregarDashboard = useCallback(async () => {
    if (!idCondo) return;
    setDashLoading(true);
    try {
      const r = await contasReceberService.dashboardOrigens({ idCondominio: idCondo });
      setDashData(r);
    } catch (e) { console.error(e); }
    finally { setDashLoading(false); }
  }, [idCondo]);

  useEffect(() => { if (aba === "dashboard" && idCondo) carregarDashboard(); }, [aba, idCondo, carregarDashboard]);

  // Cards de totais clicáveis
  const filtroStatusAtual = filtros.status;
  const aplicarFiltroCard = (st) => setF("status", filtroStatusAtual === st ? "" : st);

  const totalCards = useMemo(() => ([
    { id: "todos",    label: "Total Lançado",  val: totais.totalLancado,  bg: "#EFF6FF", color: "var(--primary)", st: "" },
    { id: "receb",    label: "Total Recebido", val: totais.totalRecebido, bg: "#DCFCE7", color: "#166534",          st: "Recebido" },
    { id: "aberto",   label: "Em Aberto",      val: totais.totalAberto,   bg: "#FEF9C3", color: "#854D0E",          st: "Aberto" },
    { id: "vencido",  label: "Vencido",        val: totais.totalVencido,  bg: "#FEE2E2", color: "#991B1B",          st: "Vencido" },
  ]), [totais]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <i className="bi bi-arrow-down-circle me-2" style={{ color: "var(--primary)", fontSize: 22 }} />
            Contas a Receber
          </h1>
          <p className="page-subtitle">
            {condoNome && <strong>{condoNome} — </strong>}
            Controle de receitas, recebimentos e integração com Boletos, Reservas, Multas e Consumo
          </p>
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          {aba === "lancamentos" && (
            <button className="btn btn-primary" onClick={() => { setEditData(null); setShowModal(true); }} disabled={!idCondo || !canEdit}>
              <i className="bi bi-plus-lg" /> Novo Lançamento Avulso
            </button>
          )}
        </div>
      </div>

      {banner && (
        <Banner tipo={banner.tipo} onClose={() => setBanner(null)}>
          {banner.msg}
        </Banner>
      )}

      {/* Cards de totais */}
      {aba === "lancamentos" && idCondo && (
        <div className="row g-3 mb-4">
          {totalCards.map(t => {
            const ativo = filtroStatusAtual === t.st && t.st;
            return (
              <div className="col-6 col-md-3" key={t.id}>
                <div
                  className="stat-card"
                  style={{
                    cursor: "pointer",
                    border: ativo ? `2px solid ${t.color}` : "1px solid transparent",
                    boxShadow: ativo ? "0 4px 8px rgba(0,0,0,.08)" : "",
                    transition: "all .15s",
                  }}
                  onClick={() => aplicarFiltroCard(t.st)}
                  title={t.st ? `Clique para ${ativo ? "remover filtro" : "filtrar"} ${t.label}` : ""}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: t.color }}>{fmtMoeda(t.val || 0)}</div>
                    <div className="stat-label">{t.label}</div>
                  </div>
                  <div style={{
                    width: 40, height: 40, borderRadius: 8, background: t.bg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <i className="bi bi-arrow-down-circle" style={{ fontSize: 20, color: t.color }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="card">
        {/* Sub-abas */}
        <div style={{ borderBottom: "1px solid var(--gray-200)", padding: "0 16px", display: "flex", gap: 4, flexWrap: "wrap" }}>
          {[
            { id: "lancamentos", label: "Lançamentos", icon: "bi-list-ul" },
            { id: "relatorio",   label: "Relatório",   icon: "bi-bar-chart-line" },
            { id: "dashboard",   label: "Receitas por Origem", icon: "bi-pie-chart" },
          ].map(t => (
            <button
              key={t.id}
              className="btn border-0 d-flex align-items-center gap-1"
              style={{
                fontSize: 13, padding: "12px 14px", borderRadius: 0,
                borderBottom: aba === t.id ? "2px solid var(--primary)" : "2px solid transparent",
                fontWeight: aba === t.id ? 600 : 400,
                color: aba === t.id ? "var(--primary)" : "var(--gray-500)",
                background: "transparent",
              }}
              onClick={() => setAba(t.id)}
            >
              <i className={`bi ${t.icon}`} />{t.label}
            </button>
          ))}
        </div>

        {aba === "lancamentos" && (
          <>
            {/* Filtros */}
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--gray-100)", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "var(--gray-500)", display: "block", marginBottom: 2 }}>Vencimento de</label>
                <input type="date" className="form-control form-control-sm" style={{ width: 140 }} value={filtros.dtVencIni} onChange={e => setF("dtVencIni", e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "var(--gray-500)", display: "block", marginBottom: 2 }}>Até</label>
                <input type="date" className="form-control form-control-sm" style={{ width: 140 }} value={filtros.dtVencFim} onChange={e => setF("dtVencFim", e.target.value)} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "var(--gray-500)", display: "block", marginBottom: 2 }}>Status</label>
                <select className="form-select form-select-sm" style={{ width: "auto" }} value={filtros.status} onChange={e => setF("status", e.target.value)}>
                  <option value="">Todos</option>
                  <option value="Aberto">Em Aberto</option>
                  <option value="Vencido">Vencido</option>
                  <option value="RecebidoParcial">Parcial</option>
                  <option value="Recebido">Recebido</option>
                  <option value="Cancelado">Cancelado</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "var(--gray-500)", display: "block", marginBottom: 2 }}>Tipo</label>
                <select className="form-select form-select-sm" style={{ width: "auto" }} value={filtros.tpReceita} onChange={e => setF("tpReceita", e.target.value)}>
                  <option value="">Todos</option>
                  {Object.entries(TP_RECEITA_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: "var(--gray-500)", display: "block", marginBottom: 2 }}>Origem</label>
                <select className="form-select form-select-sm" style={{ width: "auto" }} value={filtros.tpOrigem} onChange={e => setF("tpOrigem", e.target.value)}>
                  <option value="">Todas</option>
                  {Object.entries(ORIGEM_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div style={{ flex: "1 1 200px", minWidth: 180 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "var(--gray-500)", display: "block", marginBottom: 2 }}>Buscar</label>
                <input
                  type="text" className="form-control form-control-sm"
                  placeholder="Descrição, nº doc. ou condômino..."
                  value={filtros.search}
                  onChange={e => setF("search", e.target.value)}
                />
              </div>
              <button className="btn btn-outline-secondary btn-sm" onClick={carregar} title="Atualizar">
                <i className="bi bi-arrow-clockwise" />
              </button>
            </div>

            {/* Tabela */}
            <div className="table-responsive">
              <table className="table table-custom">
                <thead><tr>
                  <th style={{ width: 32 }}></th>
                  <th>Descrição</th>
                  <th style={{ width: 110 }}>Origem</th>
                  <th>Tipo</th>
                  <th>Condômino</th>
                  <th>Parcelas</th>
                  <th style={{ textAlign: "right" }}>Total</th>
                  <th style={{ textAlign: "right" }}>Recebido</th>
                  <th>Status</th>
                  <th style={{ width: 130 }}>Ações</th>
                </tr></thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={10} className="text-center py-5">
                      <span className="spinner-border spinner-border-sm text-primary me-2" />
                    </td></tr>
                  ) : !idCondo ? (
                    <tr><td colSpan={10} className="text-center py-5" style={{ color: "var(--gray-400)" }}>
                      <i className="bi bi-buildings" style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
                      Selecione um condomínio
                    </td></tr>
                  ) : data.length === 0 ? (
                    <tr><td colSpan={10} className="text-center py-5" style={{ color: "var(--gray-400)" }}>
                      <i className="bi bi-arrow-down-circle" style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
                      Nenhum lançamento encontrado
                    </td></tr>
                  ) : data.map(cr => {
                    const orig = ORIGEM_META[cr.tpOrigem || "Avulso"];
                    const podeBaixarAvulso = cr.podeBaixarAvulso !== false;
                    return (
                      <React.Fragment key={cr.id}>
                        <tr style={{ background: expandidos[cr.id] ? "#F8FAFF" : "" }}>
                          <td>
                            <button className="btn btn-sm btn-outline-secondary" style={{ padding: "1px 6px", fontSize: 12 }}
                              onClick={() => setExpandidos(p => ({ ...p, [cr.id]: !p[cr.id] }))}>
                              <i className={`bi bi-chevron-${expandidos[cr.id] ? "up" : "down"}`} />
                            </button>
                          </td>
                          <td>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{cr.descricao}</div>
                            <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 2 }}>
                              {cr.nrDocumento && (
                                <span style={{ fontSize: 11.5, color: "var(--gray-400)", fontFamily: "monospace" }}>
                                  Nº {cr.nrDocumento}
                                </span>
                              )}
                              {Number(cr.cobradoBoleto) === 1 && (
                                <span style={{ background: "#FEF3C7", color: "#92400E", padding: "1px 6px", borderRadius: 8, fontSize: 10, fontWeight: 600 }}>
                                  <i className="bi bi-receipt me-1" />Em boleto
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span style={{
                              background: orig.bg, color: orig.color,
                              padding: "2px 8px", borderRadius: 12,
                              fontSize: 11, fontWeight: 600,
                              display: "inline-flex", alignItems: "center", gap: 4,
                            }} title={cr.refOrigemFonte}>
                              <i className={`bi ${orig.icon}`} />{orig.label}
                            </span>
                          </td>
                          <td>
                            <span style={{ background:"var(--bg-highlight)", color: "var(--primary)", padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600 }}>
                              {TP_RECEITA_LABELS[cr.tpReceita] || cr.tpReceita}
                            </span>
                          </td>
                          <td style={{ fontSize: 12.5 }}>
                            {cr.nmCondomino || <span style={{ color: "var(--gray-300)" }}>—</span>}
                            {cr.unidade && (
                              <div style={{ fontSize: 11, color: "var(--gray-400)" }}>
                                {cr.bloco ? `Bl ${cr.bloco} — ` : ""}Un {cr.unidade}
                              </div>
                            )}
                          </td>
                          <td>
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                              {(cr.parcelas || []).map(p => {
                                const tag = badgeDias(p);
                                const cor = STATUS_PAR[p.status] || STATUS_PAR.Aberto;
                                const podeClicar = ["Aberto", "Vencido"].includes(p.status) && podeBaixarAvulso;
                                return (
                                  <span
                                    key={p.id}
                                    style={{
                                      ...cor,
                                      padding: "2px 7px", borderRadius: 10, fontSize: 11, fontWeight: 600,
                                      cursor: podeClicar ? "pointer" : "default",
                                      position: "relative",
                                    }}
                                    title={[
                                      `Parcela ${p.nrParcela} — ${fmtDt(p.dtVencimento)}`,
                                      fmtMoeda(p.vlrParcela),
                                      tag ? tag.txt : "",
                                      !podeBaixarAvulso ? "Baixe pelo Controle de Boletos" : "",
                                    ].filter(Boolean).join(" | ")}
                                    onClick={() => {
                                      if (!podeClicar) {
                                        if (!podeBaixarAvulso) {
                                          showBanner("info", "Esta parcela está vinculada a um boleto. Realize a baixa em Controle de Boletos.");
                                        }
                                        return;
                                      }
                                      setParcelaReceber(p);
                                      setParcelaDescricao(cr.descricao);
                                      setParcelaCobradoBoleto(Number(cr.cobradoBoleto) === 1);
                                      setShowReceber(true);
                                    }}
                                  >
                                    {p.nrParcela}/{cr.parcelas.length}
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                          <td style={{ textAlign: "right", fontWeight: 600, fontSize: 13 }}>{fmtMoeda(cr.vlrTotal)}</td>
                          <td style={{ textAlign: "right", fontWeight: 600, color: "#166534", fontSize: 13 }}>{fmtMoeda(cr.vlrRecebido)}</td>
                          <td>
                            <span style={{
                              background: cr.status === "Recebido" ? "#DCFCE7"
                                : cr.status === "Cancelado" ? "#F1F5F9"
                                : cr.status === "RecebidoParcial" ? "#DBEAFE"
                                : cr.status === "Vencido" ? "#FEE2E2"
                                : "#FEF9C3",
                              color: cr.status === "Recebido" ? "#166534"
                                : cr.status === "Cancelado" ? "#64748B"
                                : cr.status === "RecebidoParcial" ? "#1E40AF"
                                : cr.status === "Vencido" ? "#991B1B"
                                : "#854D0E",
                              padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600,
                            }}>
                              {cr.status === "RecebidoParcial" ? "Parcial" : cr.status}
                            </span>
                          </td>
                          <td>
                            <div className="table-actions">
                              {!["Recebido", "Cancelado"].includes(cr.status) && (
                                <button className="btn btn-outline-secondary btn-sm btn-icon"
                                  title="Editar"
                                  onClick={() => { setEditData(cr); setShowModal(true); }}
                                  disabled={!canEdit}>
                                  <i className="bi bi-pencil" />
                                </button>
                              )}
                              {cr.tpOrigem === "Avulso" && (
                                <button className="btn btn-outline-secondary btn-sm btn-icon"
                                  title="Duplicar lançamento avulso"
                                  onClick={() => handleDuplicar(cr)}
                                  disabled={!canEdit}>
                                  <i className="bi bi-files" />
                                </button>
                              )}
                              {!["Recebido", "Cancelado"].includes(cr.status) && Number(cr.cobradoBoleto) !== 1 && (
                                <button className="btn btn-outline-danger btn-sm btn-icon"
                                  title="Cancelar"
                                  onClick={() => { setCancelTarget(cr); setShowCancel(true); }}>
                                  <i className="bi bi-x-circle" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>

                        {/* Linha expandida — detalhe das parcelas */}
                        {expandidos[cr.id] && (
                          <tr><td colSpan={10} style={{ background: "#F8FAFF", padding: "0 16px 12px 48px" }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--primary)", margin: "8px 0", display: "flex", alignItems: "center", gap: 8 }}>
                              <span>Detalhamento das Parcelas</span>
                              {cr.refOrigemFonte && (
                                <span style={{ fontSize: 11, fontWeight: 400, color: "var(--gray-500)" }}>
                                  Origem: {cr.refOrigemFonte}
                                </span>
                              )}
                            </div>
                            <table style={{ width: "100%", fontSize: 12.5 }}>
                              <thead><tr style={{ color: "var(--gray-500)", fontWeight: 600 }}>
                                <th style={{ padding: "4px 8px" }}>Parcela</th>
                                <th style={{ padding: "4px 8px" }}>Vencimento</th>
                                <th style={{ padding: "4px 8px", textAlign: "right" }}>Valor</th>
                                <th style={{ padding: "4px 8px", textAlign: "right" }}>Recebido</th>
                                <th style={{ padding: "4px 8px" }}>Dt. Recebimento</th>
                                <th style={{ padding: "4px 8px" }}>Forma</th>
                                <th style={{ padding: "4px 8px" }}>Status</th>
                                <th style={{ padding: "4px 8px" }}>Ação</th>
                              </tr></thead>
                              <tbody>
                                {(cr.parcelas || []).map(p => {
                                  const tag = badgeDias(p);
                                  return (
                                    <tr key={p.id}>
                                      <td style={{ padding: "4px 8px", fontWeight: 600 }}>{p.nrParcela}/{cr.parcelas.length}</td>
                                      <td style={{ padding: "4px 8px" }}>
                                        {fmtDt(p.dtVencimento)}
                                        {tag && (
                                          <span style={{ marginLeft: 6, background: tag.background, color: tag.color, padding: "1px 6px", borderRadius: 8, fontSize: 10, fontWeight: 600 }}>
                                            {tag.txt}
                                          </span>
                                        )}
                                      </td>
                                      <td style={{ padding: "4px 8px", textAlign: "right", fontWeight: 600 }}>{fmtMoeda(p.vlrParcela)}</td>
                                      <td style={{ padding: "4px 8px", textAlign: "right", color: "#166534" }}>{p.vlrRecebido ? fmtMoeda(p.vlrRecebido) : "—"}</td>
                                      <td style={{ padding: "4px 8px" }}>{fmtDt(p.dtRecebimento)}</td>
                                      <td style={{ padding: "4px 8px" }}>{p.tpRecebimento || "—"}</td>
                                      <td style={{ padding: "4px 8px" }}>
                                        <span style={{ ...(STATUS_PAR[p.status] || STATUS_PAR.Aberto), padding: "2px 7px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                                          {STATUS_PAR[p.status]?.label || p.status}
                                        </span>
                                      </td>
                                      <td style={{ padding: "4px 8px", whiteSpace: "nowrap" }}>
                                        {["Aberto", "Vencido"].includes(p.status) && podeBaixarAvulso && (
                                          <button className="btn btn-success btn-sm" style={{ fontSize: 11, padding: "2px 10px" }}
                                            onClick={() => {
                                              setParcelaReceber(p);
                                              setParcelaDescricao(cr.descricao);
                                              setParcelaCobradoBoleto(Number(cr.cobradoBoleto) === 1);
                                              setShowReceber(true);
                                            }}>
                                            <i className="bi bi-cash me-1" />Receber
                                          </button>
                                        )}
                                        {p.status === "Recebido" && (
                                          <button className="btn btn-outline-warning btn-sm" style={{ fontSize: 11, padding: "2px 10px" }}
                                            onClick={() => abrirEstornar(p)}
                                            title="Estornar este recebimento (cria reversão na conta bancária)">
                                            <i className="bi bi-arrow-counterclockwise me-1" />Estornar
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </td></tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {pagination.totalPages > 1 && (
              <div className="pagination-wrapper">
                <span className="pagination-info">{pagination.total} registros</span>
                <nav><ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${filtros.page === 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setF("page", filtros.page - 1)}>
                      <i className="bi bi-chevron-left" />
                    </button>
                  </li>
                  {Array.from({ length: pagination.totalPages }, (_, i) => (
                    <li key={i + 1} className={`page-item ${filtros.page === i + 1 ? "active" : ""}`}>
                      <button className="page-link" onClick={() => setF("page", i + 1)}>{i + 1}</button>
                    </li>
                  ))}
                  <li className={`page-item ${filtros.page === pagination.totalPages ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setF("page", filtros.page + 1)}>
                      <i className="bi bi-chevron-right" />
                    </button>
                  </li>
                </ul></nav>
              </div>
            )}
          </>
        )}

        {/* Relatório */}
        {aba === "relatorio" && (
          <div style={{ padding: 16 }}>
            <div className="form-section">
              <div className="form-section-title"><i className="bi bi-funnel" /> Filtros</div>
              <div className="row g-3 align-items-end">
                <div className="col-md-2">
                  <label className="form-label">Vencimento de</label>
                  <input type="date" className="form-control" value={relFiltros.dtVencIni} onChange={e => setRelFiltros(p => ({ ...p, dtVencIni: e.target.value }))} />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Até</label>
                  <input type="date" className="form-control" value={relFiltros.dtVencFim} onChange={e => setRelFiltros(p => ({ ...p, dtVencFim: e.target.value }))} />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={relFiltros.status} onChange={e => setRelFiltros(p => ({ ...p, status: e.target.value }))}>
                    <option value="">Todos</option>
                    <option value="Aberto">Em Aberto</option>
                    <option value="Vencido">Vencido</option>
                    <option value="Recebido">Recebido</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Tipo</label>
                  <select className="form-select" value={relFiltros.tpReceita} onChange={e => setRelFiltros(p => ({ ...p, tpReceita: e.target.value }))}>
                    <option value="">Todos</option>
                    {Object.entries(TP_RECEITA_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Agrupar por</label>
                  <select className="form-select" value={relFiltros.agrupar} onChange={e => setRelFiltros(p => ({ ...p, agrupar: e.target.value }))}>
                    <option value="tpReceita">Tipo de Receita</option>
                    <option value="condomino">Condômino</option>
                    <option value="periodo">Período</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <button className="btn btn-primary w-100" onClick={gerarRelatorio} disabled={relLoading || !idCondo}>
                    {relLoading ? <span className="spinner-border spinner-border-sm" />
                      : <><i className="bi bi-bar-chart-line me-1" />Gerar</>}
                  </button>
                </div>
              </div>
            </div>
            {relData && (
              <>
                <div className="row g-3 mb-3">
                  {[
                    { label: "Total Lançado",  val: relData.totais?.totalLancado,  color: "var(--primary)", bg: "#EFF6FF" },
                    { label: "Recebido",       val: relData.totais?.totalRecebido, color: "#166534",        bg: "#DCFCE7" },
                    { label: "Em Aberto",      val: relData.totais?.totalAberto,   color: "#854D0E",        bg: "#FEF9C3" },
                    { label: "Vencido",        val: relData.totais?.totalVencido,  color: "#991B1B",        bg: "#FEE2E2" },
                  ].map(t => (
                    <div className="col-6 col-md-3" key={t.label}>
                      <div style={{ background: t.bg, borderRadius: 10, padding: "14px 16px" }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: t.color }}>{fmtMoeda(t.val || 0)}</div>
                        <div style={{ fontSize: 12, color: t.color, opacity: 0.8 }}>{t.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {relData.grupos?.map((grp, i) => (
                  <div key={i} className="card mb-3">
                    <div style={{ padding: "10px 16px", background: "var(--gray-50)", borderBottom: "1px solid var(--gray-200)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: "var(--gray-700)" }}>{grp.grupo}</span>
                      <div style={{ display: "flex", gap: 12, fontSize: 12.5 }}>
                        <span style={{ color: "#166534", fontWeight: 600 }}>Recebido: {fmtMoeda(grp.totalRecebido)}</span>
                        <span style={{ color: "#854D0E", fontWeight: 600 }}>Aberto: {fmtMoeda(grp.totalAberto)}</span>
                        <span style={{ color: "var(--primary)", fontWeight: 700 }}>Total: {fmtMoeda(grp.totalGrupo)}</span>
                      </div>
                    </div>
                    <table className="table table-custom mb-0" style={{ fontSize: 12.5 }}>
                      <thead><tr>
                        <th>Descrição</th><th>Condômino</th><th>Parcela</th>
                        <th>Vencimento</th>
                        <th style={{ textAlign: "right" }}>Valor</th>
                        <th>Status</th><th>Recebimento</th>
                      </tr></thead>
                      <tbody>
                        {grp.parcelas?.map((p, j) => (
                          <tr key={j}>
                            <td>{p.descricao}</td>
                            <td style={{ fontSize: 12 }}>{p.nmCondomino || "—"}</td>
                            <td>{p.nrParcela}/{p.totalParcelas}</td>
                            <td>{fmtDt(p.dtVencimento)}</td>
                            <td style={{ textAlign: "right", fontWeight: 600 }}>{fmtMoeda(p.vlrParcela)}</td>
                            <td>
                              <span style={{ ...(STATUS_PAR[p.status] || STATUS_PAR.Aberto), padding: "2px 7px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                                {STATUS_PAR[p.status]?.label || p.status}
                              </span>
                            </td>
                            <td style={{ fontSize: 12 }}>{fmtDt(p.dtRecebimento)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* Dashboard de Receitas por Origem */}
        {aba === "dashboard" && (
          <div style={{ padding: 16 }}>
            {dashLoading ? (
              <div className="text-center py-5">
                <span className="spinner-border text-primary" />
              </div>
            ) : !dashData ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-pie-chart" style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
                Carregando...
              </div>
            ) : (
              <>
                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <div style={{ background:"var(--bg-success-soft)", borderRadius: 10, padding: "14px 16px" }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: "#166534" }}>
                        {fmtMoeda(dashData.totais?.totalRecebido || 0)}
                      </div>
                      <div style={{ fontSize: 12, color: "#166534", opacity: 0.85 }}>Recebido no período</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div style={{ background:"var(--bg-warning-soft)", borderRadius: 10, padding: "14px 16px" }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: "#854D0E" }}>
                        {fmtMoeda(dashData.totais?.totalEmAberto || 0)}
                      </div>
                      <div style={{ fontSize: 12, color: "#854D0E", opacity: 0.85 }}>Em aberto</div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div style={{ background:"var(--bg-danger-soft)", borderRadius: 10, padding: "14px 16px" }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: "#991B1B" }}>
                        {fmtMoeda(dashData.totais?.totalVencido || 0)}
                      </div>
                      <div style={{ fontSize: 12, color: "#991B1B", opacity: 0.85 }}>Vencido</div>
                    </div>
                  </div>
                </div>

                <h6 style={{ fontWeight: 700, color: "var(--gray-700)", marginBottom: 12 }}>
                  <i className="bi bi-pie-chart me-2" />Receitas por Origem — {fmtDt(dashData.periodo?.dtIni)} a {fmtDt(dashData.periodo?.dtFim)}
                </h6>

                {(dashData.origens || []).length === 0 ? (
                  <div className="text-center py-4 text-muted" style={{ fontSize: 13 }}>
                    Sem recebimentos no período.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-custom" style={{ fontSize: 13 }}>
                      <thead><tr>
                        <th style={{ width: 200 }}>Origem</th>
                        <th style={{ textAlign: "right" }}>Qtd. Parcelas</th>
                        <th style={{ textAlign: "right" }}>Total Recebido</th>
                        <th style={{ width: 200 }}>% do Total</th>
                      </tr></thead>
                      <tbody>
                        {dashData.origens.map(o => {
                          const meta = ORIGEM_META[o.tpOrigem] || ORIGEM_META.Avulso;
                          const pct = dashData.totais?.totalRecebido > 0
                            ? (parseFloat(o.totalRecebido) / parseFloat(dashData.totais.totalRecebido)) * 100
                            : 0;
                          return (
                            <tr key={o.tpOrigem}>
                              <td>
                                <span style={{
                                  background: meta.bg, color: meta.color,
                                  padding: "4px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
                                  display: "inline-flex", alignItems: "center", gap: 6,
                                }}>
                                  <i className={`bi ${meta.icon}`} />{meta.label}
                                </span>
                              </td>
                              <td style={{ textAlign: "right" }}>{o.qtdParcelas}</td>
                              <td style={{ textAlign: "right", fontWeight: 700, color: "#166534" }}>
                                {fmtMoeda(o.totalRecebido)}
                              </td>
                              <td>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <div style={{ flex: 1, height: 8, background: "var(--gray-100)", borderRadius: 4, overflow: "hidden" }}>
                                    <div style={{ width: `${Math.min(pct, 100)}%`, height: "100%", background: meta.color }} />
                                  </div>
                                  <span style={{ fontSize: 12, color: "var(--gray-600)", minWidth: 50, textAlign: "right" }}>
                                    {pct.toFixed(1)}%
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <ContaReceberModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSave}
        editData={editData}
        saving={saving}
        idCondominio={idCondo}
        condominioNome={condoNome}
      />
      <ReceberParcelaModal
        show={showReceber}
        onClose={() => { setShowReceber(false); setParcelaReceber(null); }}
        parcela={parcelaReceber}
        onRecebido={handleRecebido}
        idCondominio={idCondo}
        descricao={parcelaDescricao}
        cobradoBoleto={parcelaCobradoBoleto}
      />
      <DeleteModal
        show={showCancel}
        onClose={() => setShowCancel(false)}
        onConfirm={handleCancelar}
        itemName={cancelTarget?.descricao}
        loading={cancelling}
      />

      {/* Modal de estorno com motivo (substitui window.prompt) */}
      {showEstornar && (
        <div className="modal-overlay" onClick={() => !estornarSaving && setShowEstornar(false)}>
          <div className="modal-box" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h6 className="modal-title">
                <i className="bi bi-arrow-counterclockwise me-2" />
                Estornar Recebimento
              </h6>
              <button className="btn-close" onClick={() => setShowEstornar(false)} disabled={estornarSaving} />
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color:"var(--gray-500)", marginBottom: 10 }}>
                Parcela <strong>{estornarTarget?.nrParcela}</strong> — {fmtMoeda(estornarTarget?.vlrRecebido)}.
                O estorno marcará a transação bancária como <strong>Estornada</strong> (preservando histórico) e reabrirá a parcela.
              </p>
              <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                Motivo do estorno <span style={{ color: "#991B1B" }}>*</span>
              </label>
              <textarea
                className="form-control"
                rows={3}
                value={estornarMotivo}
                onChange={e => setEstornarMotivo(e.target.value)}
                placeholder="Ex.: pagamento em duplicidade, valor incorreto, cancelamento pelo condômino..."
                autoFocus
              />
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowEstornar(false)}
                disabled={estornarSaving}>
                Cancelar
              </button>
              <button
                className="btn btn-warning btn-sm"
                onClick={confirmarEstorno}
                disabled={estornarSaving || !estornarMotivo.trim()}>
                {estornarSaving ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-arrow-counterclockwise me-1" />}
                Confirmar Estorno
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
